import { FormEvent, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { PlatformApiError } from "../api";
import { SupportApiClient } from "../support-api";
import {
  ActionBar,
  AdaptiveGrid,
  DetailsList,
  FieldGroup,
  FormLayout,
  Page,
  PageHeader,
  Section,
  SectionHeader,
  StatusState,
} from "../adaptive/primitives";
import type { AppRoute } from "../routes";
import type { ProductManifest } from "../types";
import { CrmApiClient } from "./api";
import { CrmInsightsPanel } from "./CrmInsightsPanel";
import type {
  CrmBusiness,
  CrmBusiness360,
  CrmContact,
  CrmOpportunity,
  CrmOpportunityStage,
  CrmPipeline,
  CrmSummary,
  CrmTask,
} from "./types";

const STAGES: readonly CrmOpportunityStage[] = [
  "NEW",
  "CONTACTED",
  "QUALIFIED",
  "DEMO",
  "PROPOSAL",
  "NEGOTIATION",
  "WON",
  "LOST",
];

const STAGE_LABEL: Record<CrmOpportunityStage, string> = {
  NEW: "Nueva",
  CONTACTED: "Contactado",
  QUALIFIED: "Calificado",
  DEMO: "Demo",
  PROPOSAL: "Propuesta",
  NEGOTIATION: "Negociación",
  WON: "Ganada",
  LOST: "Perdida",
};

const LIFECYCLE_LABEL = {
  prospect: "Prospecto",
  customer: "Cliente",
  inactive: "Inactivo",
} as const;

type BusinessTab =
  | "summary"
  | "insights"
  | "opportunities"
  | "contacts"
  | "tasks"
  | "activity"
  | "products";

type BusinessForm = "business" | "opportunity" | "contact" | "task" | null;
type BusinessCollectionView = "table" | "gallery";

function money(minor: number | null, currency = "ARS"): string {
  if (minor === null) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(minor / 100);
}

function dateTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function dateOnly(value: string): string {
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(new Date(value));
}

function localDateTimeInput(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "No se pudo completar la operación.";
}

function lifecycleTone(lifecycle: CrmBusiness["lifecycle"]): string {
  if (lifecycle === "customer") return "success";
  if (lifecycle === "inactive") return "neutral";
  return "warning";
}

function stageTone(stage: CrmOpportunityStage): string {
  if (stage === "WON") return "success";
  if (stage === "LOST") return "neutral";
  if (stage === "PROPOSAL" || stage === "NEGOTIATION") return "warning";
  return "active";
}

function allowedStages(current: CrmOpportunityStage): readonly CrmOpportunityStage[] {
  if (current === "WON") return ["WON"];
  if (current === "LOST") return ["LOST", "NEW"];
  const currentIndex = STAGES.indexOf(current);
  return STAGES.filter((stage, index) => stage === "LOST" || stage === "WON" || (index >= currentIndex && stage !== "LOST"));
}

function CrmNav({
  section,
  navigate,
}: {
  section: Extract<AppRoute, { kind: "crm" }>["section"];
  navigate: (path: string) => void;
}) {
  const items = [
    ["home", "Hoy", "/crm"],
    ["businesses", "Negocios", "/crm/businesses"],
    ["pipeline", "Pipeline", "/crm/pipeline"],
    ["tasks", "Tareas", "/crm/tasks"],
  ] as const;
  const active = section === "business" ? "businesses" : section;
  return (
    <nav className="crm-subnav" aria-label="CRM ClickDialog" data-nav-kind="staff">
      {items.map(([key, label, href]) => (
        <a
          key={key}
          href={href}
          aria-current={active === key ? "page" : undefined}
          onClick={(event) => {
            event.preventDefault();
            navigate(href);
          }}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

function Loading({ title = "Cargando CRM…" }: { title?: string }) {
  return (
    <StatusState
      tone="loading"
      title={title}
      message="Consultando la operación comercial de ClickDialog."
    />
  );
}

function CrmError({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <StatusState
      tone="danger"
      title="No se pudo cargar el CRM"
      message={message}
      action={retry ? <button onClick={retry}>Reintentar</button> : undefined}
    />
  );
}

function Metric({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="crm-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      {hint && <small>{hint}</small>}
    </article>
  );
}

function CrmHome({
  api,
  navigate,
  onExpired,
}: {
  api: CrmApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
}) {
  const [summary, setSummary] = useState<CrmSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSummary(await api.summary());
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorMessage(cause));
    }
  }, [api, onExpired]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <CrmError message={error} retry={() => void load()} />;
  if (!summary) return <Loading />;

  const openPipeline = Object.entries(summary.pipeline)
    .filter(([stage]) => stage !== "WON" && stage !== "LOST")
    .reduce((sum, [, value]) => sum + value.count, 0);

  return (
    <>
      <PageHeader
        eyebrow="Operación comercial"
        title="Hoy"
        description="Qué necesita atención para avanzar ventas y clientes."
        actions={
          <button className="primary-button" onClick={() => navigate("/crm/businesses")}>
            + Nuevo negocio
          </button>
        }
      />
      <AdaptiveGrid compact={2} medium={4} wide={4} className="crm-metrics">
        <Metric label="Prospectos" value={summary.businesses.prospect ?? 0} />
        <Metric label="Clientes" value={summary.businesses.customer ?? 0} />
        <Metric label="Oportunidades abiertas" value={openPipeline} />
        <Metric
          label="Tareas vencidas"
          value={summary.dueTasks}
          hint={summary.dueTasks > 0 ? "Requieren atención" : "Todo al día"}
        />
      </AdaptiveGrid>

      <Section labelledBy="crm-today-title">
        <SectionHeader
          titleId="crm-today-title"
          eyebrow="Agenda"
          title="Próximas acciones"
          description="Tareas abiertas con vencimiento más cercano."
          actions={<button onClick={() => navigate("/crm/tasks")}>Ver todas</button>}
        />
        {summary.nextTasks.length === 0 ? (
          <StatusState
            title="No hay tareas próximas"
            message="Creá una tarea desde la ficha de un negocio para mantener el seguimiento comercial."
          />
        ) : (
          <AdaptiveGrid compact={1} medium={2} wide={3}>
            {summary.nextTasks.map((task) => (
              <article className="crm-card" key={task.id}>
                <div className="crm-card-head">
                  <span className={`crm-pill ${task.priority}`}>{task.priority}</span>
                  <small>{dateTime(task.dueAt)}</small>
                </div>
                <h3>{task.title}</h3>
                <p>{task.notes || "Sin nota adicional."}</p>
                <button onClick={() => navigate(`/crm/businesses/${task.businessId}`)}>
                  Abrir negocio
                </button>
              </article>
            ))}
          </AdaptiveGrid>
        )}
      </Section>

      <Section labelledBy="crm-pipeline-summary-title">
        <SectionHeader
          titleId="crm-pipeline-summary-title"
          eyebrow="Pipeline"
          title="Estado de oportunidades"
          actions={<button onClick={() => navigate("/crm/pipeline")}>Abrir pipeline</button>}
        />
        <div className="crm-stage-summary">
          {STAGES.map((stage) => (
            <div key={stage}>
              <span>{STAGE_LABEL[stage]}</span>
              <strong>{summary.pipeline[stage]?.count ?? 0}</strong>
              <small>{money(summary.pipeline[stage]?.valueMinor ?? 0)}</small>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function NewBusinessForm({
  api,
  onCreated,
  onCancel,
}: {
  api: CrmApiClient;
  onCreated: (business: CrmBusiness) => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      const result = await api.createBusiness({
        name: String(form.get("name") || ""),
        category: String(form.get("category") || "") || undefined,
        address: String(form.get("address") || "") || undefined,
        zone: String(form.get("zone") || "") || undefined,
        phone: String(form.get("phone") || "") || undefined,
        whatsapp: String(form.get("whatsapp") || "") || undefined,
        email: String(form.get("email") || "") || undefined,
        website: String(form.get("website") || "") || undefined,
        googleMapsUrl: String(form.get("googleMapsUrl") || "") || undefined,
        source: "field_sales",
      });
      onCreated(result.business);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="details-card crm-form-card" onSubmit={submit}>
      <SectionHeader
        eyebrow="Alta rápida"
        title="Nuevo negocio"
        description="Cargá sólo lo que sabés ahora. Podés completar la ficha después."
      />
      {error && <div className="inline-alert" role="alert">{error}</div>}
      <FormLayout columns={2}>
        <FieldGroup><label>Nombre del negocio<input name="name" required maxLength={160} autoFocus /></label></FieldGroup>
        <FieldGroup><label>Rubro<input name="category" maxLength={120} /></label></FieldGroup>
        <FieldGroup span="full"><label>Dirección<input name="address" maxLength={320} /></label></FieldGroup>
        <FieldGroup><label>Zona<input name="zone" maxLength={120} /></label></FieldGroup>
        <FieldGroup><label>Teléfono<input name="phone" maxLength={80} /></label></FieldGroup>
        <FieldGroup><label>WhatsApp<input name="whatsapp" maxLength={80} /></label></FieldGroup>
        <FieldGroup><label>Email<input name="email" type="email" /></label></FieldGroup>
        <FieldGroup><label>Sitio web<input name="website" type="url" /></label></FieldGroup>
        <FieldGroup span="full"><label>Google Maps<input name="googleMapsUrl" type="url" placeholder="https://maps.google.com/…" /></label></FieldGroup>
      </FormLayout>
      <ActionBar sticky="compact">
        <button type="button" onClick={onCancel}>Cancelar</button>
        <button className="primary-button" disabled={busy}>{busy ? "Guardando…" : "Crear negocio"}</button>
      </ActionBar>
    </form>
  );
}

function EditBusinessForm({
  api,
  business,
  onSaved,
  onCancel,
}: {
  api: CrmApiClient;
  business: CrmBusiness;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const form = new FormData(event.currentTarget);
    try {
      await api.updateBusiness(business.id, {
        expectedVersion: business.version,
        name: String(form.get("name") || ""),
        legalName: String(form.get("legalName") || "") || null,
        category: String(form.get("category") || "") || null,
        address: String(form.get("address") || "") || null,
        zone: String(form.get("zone") || "") || null,
        phone: String(form.get("phone") || "") || null,
        whatsapp: String(form.get("whatsapp") || "") || null,
        email: String(form.get("email") || "") || null,
        website: String(form.get("website") || "") || null,
        googleMapsUrl: String(form.get("googleMapsUrl") || "") || null,
      });
      onSaved();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="details-card crm-form-card" onSubmit={submit}>
      <SectionHeader title="Editar negocio" description="Datos comerciales y de contacto del negocio." />
      {error && <div className="inline-alert" role="alert">{error}</div>}
      <FormLayout columns={2}>
        <FieldGroup><label>Nombre<input name="name" required defaultValue={business.name} /></label></FieldGroup>
        <FieldGroup><label>Razón social<input name="legalName" defaultValue={business.legalName ?? ""} /></label></FieldGroup>
        <FieldGroup><label>Rubro<input name="category" defaultValue={business.category ?? ""} /></label></FieldGroup>
        <FieldGroup><label>Zona<input name="zone" defaultValue={business.zone ?? ""} /></label></FieldGroup>
        <FieldGroup span="full"><label>Dirección<input name="address" defaultValue={business.address ?? ""} /></label></FieldGroup>
        <FieldGroup><label>Teléfono<input name="phone" defaultValue={business.phone ?? ""} /></label></FieldGroup>
        <FieldGroup><label>WhatsApp<input name="whatsapp" defaultValue={business.whatsapp ?? ""} /></label></FieldGroup>
        <FieldGroup><label>Email<input name="email" type="email" defaultValue={business.email ?? ""} /></label></FieldGroup>
        <FieldGroup><label>Web<input name="website" type="url" defaultValue={business.website ?? ""} /></label></FieldGroup>
        <FieldGroup span="full"><label>Google Maps<input name="googleMapsUrl" type="url" defaultValue={business.googleMapsUrl ?? ""} /></label></FieldGroup>
      </FormLayout>
      <ActionBar>
        <button type="button" onClick={onCancel}>Cancelar</button>
        <button className="primary-button" disabled={busy}>{busy ? "Guardando…" : "Guardar cambios"}</button>
      </ActionBar>
    </form>
  );
}

function BusinessCard({ business, navigate }: { business: CrmBusiness; navigate: (path: string) => void }) {
  return (
    <button className="crm-business-card" onClick={() => navigate(`/crm/businesses/${business.id}`)}>
      <div className="crm-card-head">
        <span className={`crm-pill ${lifecycleTone(business.lifecycle)}`}>{LIFECYCLE_LABEL[business.lifecycle]}</span>
        <small>{business.zone || business.category || "Sin zona"}</small>
      </div>
      <strong>{business.name}</strong>
      <span>{business.category || "Sin rubro"}</span>
      <div className="crm-business-meta">
        <span>{business.googleRating !== null ? `${business.googleRating.toFixed(1)} ★` : "Sin rating"}</span>
        <span>{business.googleReviewCount !== null ? `${business.googleReviewCount} reseñas` : ""}</span>
      </div>
      <small>{business.address || business.phone || business.email || "Ficha pendiente de completar"}</small>
    </button>
  );
}

function BusinessTable({ items, navigate }: { items: readonly CrmBusiness[]; navigate: (path: string) => void }) {
  return (
    <div className="crm-table-wrap">
      <table className="crm-business-table">
        <thead>
          <tr>
            <th>Negocio</th>
            <th>Estado</th>
            <th>Rubro</th>
            <th>Zona</th>
            <th>Contacto</th>
            <th>Google</th>
            <th>Actualizado</th>
            <th><span className="sr-only">Acciones</span></th>
          </tr>
        </thead>
        <tbody>
          {items.map((business) => (
            <tr key={business.id}>
              <td>
                <button className="crm-table-link" onClick={() => navigate(`/crm/businesses/${business.id}`)}>
                  {business.name}
                </button>
                <small>{business.address || "Sin dirección"}</small>
              </td>
              <td><span className={`crm-pill ${lifecycleTone(business.lifecycle)}`}>{LIFECYCLE_LABEL[business.lifecycle]}</span></td>
              <td>{business.category || "—"}</td>
              <td>{business.zone || "—"}</td>
              <td><span>{business.whatsapp || business.phone || business.email || "—"}</span></td>
              <td>{business.googleRating !== null ? <span>{business.googleRating.toFixed(1)} ★ · {business.googleReviewCount ?? 0}</span> : "—"}</td>
              <td>{dateOnly(business.updatedAt)}</td>
              <td><button onClick={() => navigate(`/crm/businesses/${business.id}`)}>Abrir</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CrmBusinesses({ api, navigate, onExpired }: { api: CrmApiClient; navigate: (path: string) => void; onExpired: () => void }) {
  const [items, setItems] = useState<readonly CrmBusiness[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [lifecycle, setLifecycle] = useState("");
  const [view, setView] = useState<BusinessCollectionView>("table");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listBusinesses({ search, lifecycle, limit: 100 });
      setItems(result.businesses);
      setTotal(result.total);
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, [api, search, lifecycle, onExpired]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), search ? 180 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  return (
    <>
      <PageHeader eyebrow="CRM" title="Negocios" description={`${total} negocios en la base comercial.`} actions={<button className="primary-button" onClick={() => setCreating(true)}>+ Nuevo negocio</button>} />
      {creating && <NewBusinessForm api={api} onCancel={() => setCreating(false)} onCreated={(business) => navigate(`/crm/businesses/${business.id}`)} />}
      <div className="crm-toolbar crm-business-toolbar">
        <label>Buscar<input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, dirección, rubro, teléfono…" /></label>
        <label>Estado<select value={lifecycle} onChange={(event) => setLifecycle(event.target.value)}><option value="">Todos</option><option value="prospect">Prospectos</option><option value="customer">Clientes</option><option value="inactive">Inactivos</option></select></label>
        <div className="crm-view-switch" role="group" aria-label="Vista de negocios">
          <button type="button" aria-pressed={view === "table"} onClick={() => setView("table")}>Tabla</button>
          <button type="button" aria-pressed={view === "gallery"} onClick={() => setView("gallery")}>Galería</button>
        </div>
      </div>
      {error ? <CrmError message={error} retry={() => void load()} /> : loading ? <Loading title="Cargando negocios…" /> : items.length === 0 ? (
        <StatusState title="No hay negocios" message="Creá el primero o cambiá los filtros." action={<button className="primary-button" onClick={() => setCreating(true)}>Nuevo negocio</button>} />
      ) : view === "table" ? <BusinessTable items={items} navigate={navigate} /> : (
        <AdaptiveGrid compact={1} medium={2} wide={3} className="crm-business-grid">{items.map((business) => <BusinessCard key={business.id} business={business} navigate={navigate} />)}</AdaptiveGrid>
      )}
    </>
  );
}

function OpportunityForm({ api, business, products, opportunity, onSaved, onCancel }: { api: CrmApiClient; business: CrmBusiness; products: readonly ProductManifest[]; opportunity?: CrmOpportunity; onSaved: () => void; onCancel: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const knownProduct = opportunity && products.some((product) => product.key === opportunity.productKey);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError(null);
    const form = new FormData(event.currentTarget);
    const amount = String(form.get("amount") || "").trim();
    const probability = String(form.get("probability") || "").trim();
    const expectedClose = String(form.get("expectedCloseAt") || "").trim();
    try {
      const payload: Record<string, unknown> = {
        productKey: String(form.get("productKey") || ""), title: String(form.get("title") || "") || undefined,
        estimatedValueMinor: amount ? Math.round(Number(amount) * 100) : null, currency: "ARS",
        probability: probability ? Number(probability) : undefined, source: String(form.get("source") || "field_sales"),
        expectedCloseAt: expectedClose ? new Date(`${expectedClose}T12:00:00`).toISOString() : null,
      };
      if (opportunity) { payload.expectedVersion = opportunity.version; await api.updateOpportunity(opportunity.id, payload); }
      else { payload.stage = "NEW"; await api.createOpportunity(business.id, payload); }
      onSaved();
    } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); }
  }

  return (
    <form className="details-card crm-form-card" onSubmit={submit}>
      <SectionHeader title={opportunity ? "Editar oportunidad" : "Nueva oportunidad"} />
      {error && <div className="inline-alert" role="alert">{error}</div>}
      <FormLayout columns={2}>
        <FieldGroup><label>Producto<select name="productKey" required defaultValue={opportunity?.productKey ?? products[0]?.key ?? "website"}>{opportunity && !knownProduct && <option value={opportunity.productKey}>{opportunity.productKey}</option>}{products.map((product) => <option key={product.key} value={product.key}>{product.displayName}</option>)}<option value="website">Sitio web</option><option value="automation">Automatización</option></select></label></FieldGroup>
        <FieldGroup><label>Valor estimado ARS<input name="amount" inputMode="numeric" type="number" min="0" step="100" defaultValue={opportunity?.estimatedValueMinor === null || opportunity?.estimatedValueMinor === undefined ? "" : opportunity.estimatedValueMinor / 100} /></label></FieldGroup>
        <FieldGroup span="full"><label>Título<input name="title" maxLength={200} required={Boolean(opportunity)} defaultValue={opportunity?.title ?? ""} placeholder={`${business.name} — oportunidad`} /></label></FieldGroup>
        <FieldGroup><label>Probabilidad %<input name="probability" type="number" min="0" max="100" step="1" defaultValue={opportunity?.probability ?? ""} /></label></FieldGroup>
        <FieldGroup><label>Cierre esperado<input name="expectedCloseAt" type="date" defaultValue={opportunity?.expectedCloseAt?.slice(0, 10) ?? ""} /></label></FieldGroup>
        <FieldGroup span="full"><label>Origen<input name="source" maxLength={80} defaultValue={opportunity?.source ?? "field_sales"} /></label></FieldGroup>
      </FormLayout>
      <ActionBar><button type="button" onClick={onCancel}>Cancelar</button><button className="primary-button" disabled={busy}>{busy ? "Guardando…" : opportunity ? "Guardar cambios" : "Crear oportunidad"}</button></ActionBar>
    </form>
  );
}

function ContactForm({ api, business, contact, onSaved, onCancel }: { api: CrmApiClient; business: CrmBusiness; contact?: CrmContact; onSaved: () => void; onCancel: () => void }) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError(null); const form = new FormData(event.currentTarget);
    const payload: Record<string, unknown> = { name: String(form.get("name") || ""), role: String(form.get("role") || "") || null, email: String(form.get("email") || "") || null, phone: String(form.get("phone") || "") || null, whatsapp: String(form.get("whatsapp") || "") || null, isPrimary: form.get("isPrimary") === "on" };
    try { if (contact) { payload.expectedVersion = contact.version; await api.updateContact(contact.id, payload); } else await api.createContact(business.id, payload); onSaved(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); }
  }
  return (
    <form className="details-card crm-form-card" onSubmit={submit}><SectionHeader title={contact ? "Editar contacto" : "Nuevo contacto"} />{error && <div className="inline-alert" role="alert">{error}</div>}
      <FormLayout columns={2}><FieldGroup><label>Nombre<input name="name" required defaultValue={contact?.name ?? ""} /></label></FieldGroup><FieldGroup><label>Rol<input name="role" placeholder="Dueño, encargada…" defaultValue={contact?.role ?? ""} /></label></FieldGroup><FieldGroup><label>Email<input name="email" type="email" defaultValue={contact?.email ?? ""} /></label></FieldGroup><FieldGroup><label>Teléfono<input name="phone" defaultValue={contact?.phone ?? ""} /></label></FieldGroup><FieldGroup><label>WhatsApp<input name="whatsapp" defaultValue={contact?.whatsapp ?? ""} /></label></FieldGroup><FieldGroup><label className="crm-checkbox"><input name="isPrimary" type="checkbox" defaultChecked={contact?.isPrimary ?? false} /> Contacto principal</label></FieldGroup></FormLayout>
      <ActionBar><button type="button" onClick={onCancel}>Cancelar</button><button className="primary-button" disabled={busy}>{busy ? "Guardando…" : contact ? "Guardar cambios" : "Guardar contacto"}</button></ActionBar>
    </form>
  );
}

function TaskForm({ api, business, opportunities, task, onSaved, onCancel }: { api: CrmApiClient; business: CrmBusiness; opportunities: readonly CrmOpportunity[]; task?: CrmTask; onSaved: () => void; onCancel: () => void }) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError(null); const form = new FormData(event.currentTarget); const due = String(form.get("dueAt") || "");
    const payload: Record<string, unknown> = { title: String(form.get("title") || ""), type: String(form.get("type") || "follow_up"), priority: String(form.get("priority") || "normal"), notes: String(form.get("notes") || "") || null, dueAt: new Date(due).toISOString(), opportunityId: String(form.get("opportunityId") || "") || null };
    try { if (task) { payload.expectedVersion = task.version; payload.status = String(form.get("status") || task.status); await api.updateTask(task.id, payload); } else await api.createTask(business.id, payload); onSaved(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); }
  }
  return (
    <form className="details-card crm-form-card" onSubmit={submit}><SectionHeader title={task ? "Editar tarea" : "Nueva tarea"} />{error && <div className="inline-alert" role="alert">{error}</div>}
      <FormLayout columns={2}>
        <FieldGroup span="full"><label>Tarea<input name="title" required defaultValue={task?.title ?? ""} placeholder="Llamar, visitar, enviar propuesta…" /></label></FieldGroup>
        <FieldGroup><label>Fecha y hora<input name="dueAt" type="datetime-local" required defaultValue={localDateTimeInput(task?.dueAt ?? null)} /></label></FieldGroup>
        <FieldGroup><label>Prioridad<select name="priority" defaultValue={task?.priority ?? "normal"}><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option><option value="low">Baja</option></select></label></FieldGroup>
        <FieldGroup><label>Tipo<select name="type" defaultValue={task?.type ?? "follow_up"}><option value="follow_up">Seguimiento</option><option value="call">Llamada</option><option value="visit">Visita</option><option value="demo">Demo</option><option value="proposal">Propuesta</option></select></label></FieldGroup>
        <FieldGroup><label>Oportunidad<select name="opportunityId" defaultValue={task?.opportunityId ?? ""}><option value="">General</option>{opportunities.filter((opportunity) => opportunity.id === task?.opportunityId || (opportunity.stage !== "WON" && opportunity.stage !== "LOST")).map((opportunity) => <option key={opportunity.id} value={opportunity.id}>{opportunity.title}</option>)}</select></label></FieldGroup>
        {task && <FieldGroup><label>Estado<select name="status" defaultValue={task.status}><option value="open">Abierta</option><option value="completed">Completada</option><option value="cancelled">Cancelada</option></select></label></FieldGroup>}
        <FieldGroup span="full"><label>Notas<textarea name="notes" rows={3} defaultValue={task?.notes ?? ""} /></label></FieldGroup>
      </FormLayout>
      <ActionBar><button type="button" onClick={onCancel}>Cancelar</button><button className="primary-button" disabled={busy}>{busy ? "Guardando…" : task ? "Guardar cambios" : "Crear tarea"}</button></ActionBar>
    </form>
  );
}

function ActivityForm({ api, business, onSaved }: { api: CrmApiClient; business: CrmBusiness; onSaved: () => void }) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent<HTMLFormElement>) { event.preventDefault(); setBusy(true); setError(null); const form = event.currentTarget; const data = new FormData(form); try { await api.addActivity(business.id, { type: String(data.get("type") || "note"), summary: String(data.get("summary") || "") }); form.reset(); onSaved(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); } }
  return <form className="crm-inline-activity" onSubmit={submit}>{error && <div className="inline-alert" role="alert">{error}</div>}<select name="type" aria-label="Tipo de actividad"><option value="note">Nota</option><option value="call">Llamada</option><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="visit">Visita</option><option value="demo">Demo</option></select><input name="summary" required placeholder="Registrar actividad…" /><button className="primary-button" disabled={busy}>Registrar</button></form>;
}

function OpportunityCard({ opportunity, api, onSaved, onEdit, onDelete }: { opportunity: CrmOpportunity; api: CrmApiClient; onSaved: () => void; onEdit: () => void; onDelete?: () => void }) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  async function move(stage: CrmOpportunityStage) { if (stage === opportunity.stage) return; setBusy(true); setError(null); try { await api.updateOpportunity(opportunity.id, { stage, expectedVersion: opportunity.version, lostReason: stage === "LOST" ? "No concretada" : undefined }); onSaved(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); } }
  return <article className="crm-card crm-opportunity-card"><div className="crm-card-head"><span className={`crm-pill ${stageTone(opportunity.stage)}`}>{STAGE_LABEL[opportunity.stage]}</span><span>{opportunity.probability ?? 0}%</span></div><h3>{opportunity.title}</h3><p>{opportunity.productKey}</p><strong>{money(opportunity.estimatedValueMinor, opportunity.currency)}</strong>{opportunity.expectedCloseAt && <small>Cierre esperado: {dateOnly(opportunity.expectedCloseAt)}</small>}{error && <small className="crm-error-text">{error}</small>}<label className="crm-stage-select">Etapa<select disabled={busy || opportunity.stage === "WON"} value={opportunity.stage} onChange={(event) => void move(event.target.value as CrmOpportunityStage)}>{allowedStages(opportunity.stage).map((stage) => <option key={stage} value={stage}>{STAGE_LABEL[stage]}</option>)}</select></label><div className="button-row"><button type="button" onClick={onEdit}>Editar</button>{onDelete && <button type="button" className="crm-danger-button" onClick={onDelete}>Eliminar</button>}</div></article>;
}

function BusinessOverview({ detail }: { detail: CrmBusiness360 }) {
  const business = detail.business; const primary = detail.contacts.find((contact) => contact.isPrimary) ?? detail.contacts[0];
  return <AdaptiveGrid compact={1} medium={2} wide={2}><section className="details-card"><h2>Negocio</h2><DetailsList items={[{ key: "category", term: "Rubro", detail: business.category || "—" },{ key: "address", term: "Dirección", detail: business.address || "—" },{ key: "zone", term: "Zona", detail: business.zone || "—" },{ key: "phone", term: "Teléfono", detail: business.phone || "—" },{ key: "whatsapp", term: "WhatsApp", detail: business.whatsapp || "—" },{ key: "email", term: "Email", detail: business.email || "—" },{ key: "website", term: "Web", detail: business.website ? <a href={business.website} target="_blank" rel="noreferrer">Abrir</a> : "—" },{ key: "maps", term: "Google Maps", detail: business.googleMapsUrl ? <a href={business.googleMapsUrl} target="_blank" rel="noreferrer">Abrir</a> : "—" },{ key: "rating", term: "Google", detail: business.googleRating !== null ? `${business.googleRating.toFixed(1)} ★ · ${business.googleReviewCount ?? 0} reseñas` : "—" }]} /></section><section className="details-card"><h2>Relación comercial</h2><DetailsList items={[{ key: "lifecycle", term: "Estado", detail: LIFECYCLE_LABEL[business.lifecycle] },{ key: "source", term: "Origen", detail: business.source },{ key: "contact", term: "Contacto principal", detail: primary ? `${primary.name}${primary.role ? ` · ${primary.role}` : ""}` : "Sin contacto" },{ key: "organization", term: "Platform", detail: detail.organization ? `${detail.organization.displayName} · ${detail.organization.status}` : "Todavía no es cliente Platform" },{ key: "opportunities", term: "Oportunidades", detail: String(detail.opportunities.length) },{ key: "products", term: "Productos activos", detail: String(detail.products.length) }]} /></section></AdaptiveGrid>;
}

function ProvisionOpportunity({ opportunity, business, api, registered, onSaved }: { opportunity: CrmOpportunity; business: CrmBusiness; api: CrmApiClient; registered: boolean; onSaved: () => void }) {
  const [nativeId, setNativeId] = useState(""); const [plan, setPlan] = useState("standard"); const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  if (!registered) return <article className="details-card crm-provision"><div><span className="crm-pill success">Venta ganada</span><h3>{opportunity.title}</h3><p>Esta venta no corresponde a un ProductKey registrado en Platform. Puede gestionarse como servicio externo o aplicación personalizada.</p></div></article>;
  async function provision() { setBusy(true); setError(null); try { await api.provisionOpportunity(opportunity.id, { nativeOrganizationId: nativeId, planKey: plan }); onSaved(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); } }
  return <article className="details-card crm-provision"><div><span className="crm-pill success">Venta ganada</span><h3>{opportunity.title}</h3><p>{business.organizationId ? "Vinculá el runtime nativo del producto con la organización Platform." : "Primero convertí el negocio en cliente Platform."}</p></div><FormLayout columns={2}><FieldGroup><label>Plan<input value={plan} onChange={(event) => setPlan(event.target.value)} /></label></FieldGroup><FieldGroup><label>ID nativo<input value={nativeId} onChange={(event) => setNativeId(event.target.value)} placeholder="organization id del producto" /></label></FieldGroup></FormLayout>{error && <div className="inline-alert" role="alert">{error}</div>}<ActionBar><button className="primary-button" disabled={!business.organizationId || busy || !nativeId} onClick={() => void provision()}>{busy ? "Activando…" : "Vincular producto"}</button></ActionBar></article>;
}

function TaskRow({ task, api, onSaved, navigate, onEdit, onDelete }: { task: CrmTask; api: CrmApiClient; onSaved: () => void; navigate: (path: string) => void; onEdit?: () => void; onDelete?: () => void }) {
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  async function complete() { setBusy(true); setError(null); try { await api.updateTask(task.id, { status: "completed", expectedVersion: task.version }); onSaved(); } catch (cause) { setError(errorMessage(cause)); } finally { setBusy(false); } }
  return <article className={`crm-task-row ${task.status}`}><div><span className={`crm-pill ${task.priority}`}>{task.priority}</span><strong>{task.title}</strong><small>{dateTime(task.dueAt)}</small>{error && <small className="crm-error-text">{error}</small>}</div><div className="button-row"><button onClick={() => navigate(`/crm/businesses/${task.businessId}`)}>Negocio</button>{onEdit && <button onClick={onEdit}>Editar</button>}{onDelete && <button className="crm-danger-button" onClick={onDelete}>Eliminar</button>}{task.status === "open" && <button className="primary-button" disabled={busy} onClick={() => void complete()}>Completar</button>}</div></article>;
}

function CrmBusinessDetail({ api, businessId, products, navigate, onExpired }: { api: CrmApiClient; businessId: string; products: readonly ProductManifest[]; navigate: (path: string) => void; onExpired: () => void }) {
  const supportApi = useMemo(() => new SupportApiClient(fetch), []);
  const [detail, setDetail] = useState<CrmBusiness360 | null>(null); const [loading, setLoading] = useState(true); const [error, setError] = useState<string | null>(null); const [tab, setTab] = useState<BusinessTab>("summary"); const [form, setForm] = useState<BusinessForm>(null); const [editingContact, setEditingContact] = useState<CrmContact | null>(null); const [editingOpportunity, setEditingOpportunity] = useState<CrmOpportunity | null>(null); const [editingTask, setEditingTask] = useState<CrmTask | null>(null); const [actionError, setActionError] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { setLoading(true); setError(null); try { setDetail(await api.getBusiness(businessId)); } catch (cause) { if (cause instanceof PlatformApiError && cause.status === 401) onExpired(); else setError(errorMessage(cause)); } finally { setLoading(false); } }, [api, businessId, onExpired]);
  useEffect(() => { void load(); }, [load]);
  if (error) return <CrmError message={error} retry={() => void load()} />; if (loading || !detail) return <Loading title="Abriendo ficha del negocio…" />;
  const business = detail.business;
  const linkedOrganization = detail.organization;
  const tabs: readonly [BusinessTab, string][] = [["summary", "Resumen"],["insights", "Inteligencia"],["opportunities", "Oportunidades"],["contacts", "Contactos"],["tasks", "Tareas"],["activity", "Actividad"],["products", "Productos"]]; const registeredProducts = new Set(products.map((product) => product.key));
  function closeEditors() { setForm(null); setEditingContact(null); setEditingOpportunity(null); setEditingTask(null); }
  async function convert() { setBusy(true); setActionError(null); try { await api.convertBusiness(business.id); await load(); } catch (cause) { setActionError(errorMessage(cause)); } finally { setBusy(false); } }
  async function toggleArchive() { setBusy(true); setActionError(null); const nextLifecycle = business.lifecycle === "inactive" ? (business.organizationId ? "customer" : "prospect") : "inactive"; try { await api.updateBusiness(business.id, { expectedVersion: business.version, lifecycle: nextLifecycle }); await load(); } catch (cause) { setActionError(errorMessage(cause)); } finally { setBusy(false); } }
  async function removeContact(contact: CrmContact) { if (!window.confirm(`¿Eliminar el contacto ${contact.name}?`)) return; setActionError(null); try { await api.deleteContact(contact.id, contact.version); if (editingContact?.id === contact.id) setEditingContact(null); await load(); } catch (cause) { setActionError(errorMessage(cause)); } }
  async function removeOpportunity(opportunity: CrmOpportunity) { if (!window.confirm(`¿Eliminar la oportunidad ${opportunity.title}?`)) return; setActionError(null); try { await api.deleteOpportunity(opportunity.id, opportunity.version); if (editingOpportunity?.id === opportunity.id) setEditingOpportunity(null); await load(); } catch (cause) { setActionError(errorMessage(cause)); } }
  async function removeTask(task: CrmTask) { if (!window.confirm(`¿Eliminar la tarea ${task.title}?`)) return; setActionError(null); try { await api.deleteTask(task.id, task.version); if (editingTask?.id === task.id) setEditingTask(null); await load(); } catch (cause) { setActionError(errorMessage(cause)); } }
  async function openSupportWorkspace() {
    if (!linkedOrganization) return;
    setBusy(true);
    setActionError(null);
    try {
      await supportApi.start({
        organizationId: linkedOrganization.id,
        reason: `Verificación desde CRM Admin · ${business.name}`,
        returnTo: `/crm/businesses/${business.id}`,
      });
      window.location.assign(`/o/${linkedOrganization.slug}/home`);
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setActionError(errorMessage(cause));
      setBusy(false);
    }
  }

  return <>
    <PageHeader eyebrow={<><button className="crm-back-link" onClick={() => navigate("/crm/businesses")}>← Negocios</button>{" · "}{LIFECYCLE_LABEL[business.lifecycle]}</>} title={business.name} description={[business.category, business.zone].filter(Boolean).join(" · ") || "Ficha comercial ClickDialog"} actions={<div className="button-row">{business.googleMapsUrl && <a className="btn" href={business.googleMapsUrl} target="_blank" rel="noreferrer">Google Maps</a>}{business.lifecycle === "prospect" && <button className="primary-button" disabled={busy} onClick={() => void convert()}>{busy ? "Convirtiendo…" : "Convertir en cliente"}</button>}<button disabled={busy} onClick={() => void toggleArchive()}>{business.lifecycle === "inactive" ? "Restaurar" : "Archivar"}</button>{linkedOrganization && <button className="primary-button" disabled={busy} onClick={() => void openSupportWorkspace()}>{busy ? "Abriendo…" : "Abrir espacio de trabajo"}</button>}</div>} />
    {actionError && <div className="inline-alert" role="alert">{actionError}</div>}
    <nav className="crm-detail-tabs" aria-label="Ficha CRM">{tabs.map(([key, label]) => <button key={key} aria-current={tab === key ? "page" : undefined} onClick={() => { setTab(key); closeEditors(); }}>{label}</button>)}</nav>

    {tab === "summary" && <Section><SectionHeader title="Resumen" description="Datos centrales y relación comercial." actions={<button onClick={() => setForm(form === "business" ? null : "business")}>{form === "business" ? "Cerrar edición" : "Editar datos"}</button>} />{form === "business" && <EditBusinessForm api={api} business={business} onCancel={() => setForm(null)} onSaved={() => { setForm(null); void load(); }} />}<BusinessOverview detail={detail} /></Section>}
    {tab === "insights" && <CrmInsightsPanel api={api} business={business} opportunities={detail.opportunities} onOpportunityCreated={() => void load()} />}

    {tab === "opportunities" && <Section><SectionHeader title="Oportunidades" description="Cada producto se vende y avanza de forma independiente." actions={<button className="primary-button" onClick={() => { setEditingOpportunity(null); setForm(form === "opportunity" ? null : "opportunity"); }}>+ Oportunidad</button>} />{(form === "opportunity" || editingOpportunity) && <OpportunityForm api={api} business={business} products={products} opportunity={editingOpportunity ?? undefined} onCancel={() => { setForm(null); setEditingOpportunity(null); }} onSaved={() => { setForm(null); setEditingOpportunity(null); void load(); }} />}{detail.opportunities.length === 0 ? <StatusState title="Sin oportunidades" message="Creá la primera oportunidad comercial para este negocio." /> : <AdaptiveGrid compact={1} medium={2} wide={3}>{detail.opportunities.map((opportunity) => <OpportunityCard key={opportunity.id} opportunity={opportunity} api={api} onSaved={() => void load()} onEdit={() => { setForm(null); setEditingOpportunity(opportunity); }} onDelete={opportunity.stage === "WON" ? undefined : () => void removeOpportunity(opportunity)} />)}</AdaptiveGrid>}</Section>}

    {tab === "contacts" && <Section><SectionHeader title="Contactos" description="Dueños, encargados y otras personas relevantes para la relación comercial." actions={<button className="primary-button" onClick={() => { setEditingContact(null); setForm(form === "contact" ? null : "contact"); }}>+ Contacto</button>} />{(form === "contact" || editingContact) && <ContactForm api={api} business={business} contact={editingContact ?? undefined} onCancel={() => { setForm(null); setEditingContact(null); }} onSaved={() => { setForm(null); setEditingContact(null); void load(); }} />}{detail.contacts.length === 0 ? <StatusState title="Sin contactos" message="Agregá dueño, encargado o cualquier persona relevante para la venta." /> : <AdaptiveGrid compact={1} medium={2} wide={3}>{detail.contacts.map((contact: CrmContact) => <article className="crm-card" key={contact.id}><div className="crm-card-head">{contact.isPrimary && <span className="crm-pill success">Principal</span>}<small>{contact.role || "Contacto"}</small></div><h3>{contact.name}</h3><p>{contact.whatsapp || contact.phone || contact.email || "Sin canal de contacto"}</p>{contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}<div className="button-row"><button type="button" onClick={() => { setForm(null); setEditingContact(contact); }}>Editar</button><button type="button" className="crm-danger-button" onClick={() => void removeContact(contact)}>Eliminar</button></div></article>)}</AdaptiveGrid>}</Section>}

    {tab === "tasks" && <Section><SectionHeader title="Tareas" description="El seguimiento deja de depender de memoria o notas sueltas." actions={<button className="primary-button" onClick={() => { setEditingTask(null); setForm(form === "task" ? null : "task"); }}>+ Tarea</button>} />{(form === "task" || editingTask) && <TaskForm api={api} business={business} opportunities={detail.opportunities} task={editingTask ?? undefined} onCancel={() => { setForm(null); setEditingTask(null); }} onSaved={() => { setForm(null); setEditingTask(null); void load(); }} />}{detail.tasks.length === 0 ? <StatusState title="Sin tareas" message="Programá la próxima acción comercial." /> : <div className="crm-task-list">{detail.tasks.map((task) => <TaskRow key={task.id} task={task} api={api} onSaved={() => void load()} navigate={navigate} onEdit={() => { setForm(null); setEditingTask(task); }} onDelete={() => void removeTask(task)} />)}</div>}</Section>}

    {tab === "activity" && <Section><SectionHeader title="Actividad" description="Historial único e inmutable de lo que ocurrió con este negocio." /><ActivityForm api={api} business={business} onSaved={() => void load()} />{detail.activities.length === 0 ? <StatusState title="Sin actividad" message="Las acciones comerciales aparecerán acá automáticamente." /> : <ol className="crm-timeline">{detail.activities.map((activity) => <li key={activity.id}><span className="crm-timeline-dot" /><div><strong>{activity.summary}</strong><small>{activity.type} · {dateTime(activity.occurredAt)}</small></div></li>)}</ol>}</Section>}

    {tab === "products" && <Section><SectionHeader title="Productos ClickDialog" description="Productos activos en Platform y ventas ganadas listas para provisionar." />{detail.products.length === 0 ? <StatusState title="Sin productos Platform" message={business.lifecycle === "customer" ? "El cliente existe en Platform pero todavía no tiene productos activados." : "Convertí el negocio en cliente para activar productos."} /> : <AdaptiveGrid compact={1} medium={2} wide={3}>{detail.products.map((product) => <article className="crm-card" key={product.id}><span className="crm-pill success">{product.status}</span><h3>{product.productKey}</h3><p>Plan {product.planKey}</p></article>)}</AdaptiveGrid>}<div className="crm-provision-list">{detail.opportunities.filter((opportunity) => opportunity.stage === "WON").map((opportunity) => <ProvisionOpportunity key={opportunity.id} opportunity={opportunity} business={business} api={api} registered={registeredProducts.has(opportunity.productKey)} onSaved={() => void load()} />)}</div></Section>}
  </>;
}

function CrmTasks({ api, navigate, onExpired }: { api: CrmApiClient; navigate: (path: string) => void; onExpired: () => void }) {
  const [tasks, setTasks] = useState<readonly CrmTask[]>([]); const [loading, setLoading] = useState(true); const [error, setError] = useState<string | null>(null); const [filter, setFilter] = useState("open");
  const load = useCallback(async () => { setLoading(true); setError(null); try { const result = await api.listTasks({ status: filter || undefined, limit: 200 }); setTasks(result.tasks); } catch (cause) { if (cause instanceof PlatformApiError && cause.status === 401) onExpired(); else setError(errorMessage(cause)); } finally { setLoading(false); } }, [api, filter, onExpired]);
  useEffect(() => { void load(); }, [load]);
  async function removeTask(task: CrmTask) { if (!window.confirm(`¿Eliminar la tarea ${task.title}?`)) return; try { await api.deleteTask(task.id, task.version); await load(); } catch (cause) { setError(errorMessage(cause)); } }
  return <><PageHeader eyebrow="CRM" title="Tareas" description="Seguimientos, llamadas, visitas, demos y propuestas." /><div className="crm-toolbar compact"><label>Estado<select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="open">Abiertas</option><option value="completed">Completadas</option><option value="cancelled">Canceladas</option><option value="">Todas</option></select></label></div>{error ? <CrmError message={error} retry={() => void load()} /> : loading ? <Loading title="Cargando tareas…" /> : tasks.length === 0 ? <StatusState title="No hay tareas" message="Las tareas creadas desde Business 360 aparecerán acá." /> : <div className="crm-task-list">{tasks.map((task) => <TaskRow key={task.id} task={task} api={api} onSaved={() => void load()} navigate={navigate} onDelete={() => void removeTask(task)} />)}</div>}</>;
}

function CrmPipelineView({ api, navigate, onExpired }: { api: CrmApiClient; navigate: (path: string) => void; onExpired: () => void }) {
  const [pipeline, setPipeline] = useState<CrmPipeline | null>(null); const [loading, setLoading] = useState(true); const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => { setLoading(true); setError(null); try { setPipeline(await api.pipeline()); } catch (cause) { if (cause instanceof PlatformApiError && cause.status === 401) onExpired(); else setError(errorMessage(cause)); } finally { setLoading(false); } }, [api, onExpired]);
  useEffect(() => { void load(); }, [load]); if (error) return <CrmError message={error} retry={() => void load()} />;
  return <><PageHeader eyebrow="CRM" title="Pipeline" description="Una oportunidad por producto. Un mismo negocio puede avanzar en varias ventas a la vez." />{loading || !pipeline ? <Loading title="Armando pipeline…" /> : <div className="crm-pipeline-board">{pipeline.columns.map((column) => <section className="crm-pipeline-column" key={column.stage}><header><span>{STAGE_LABEL[column.stage]}</span><strong>{column.items.length}</strong></header>{column.items.map(({ business, opportunity }) => <button className="crm-pipeline-card" key={opportunity.id} disabled={!business} onClick={() => business && navigate(`/crm/businesses/${business.id}`)}><span className={`crm-pill ${stageTone(column.stage)}`}>{opportunity.productKey}</span><strong>{business?.name ?? "Negocio no disponible"}</strong><small>{opportunity.title}</small><b>{money(opportunity.estimatedValueMinor, opportunity.currency)}</b></button>)}</section>)}</div>}</>;
}

export function CrmWorkspace({ route, navigate, onExpired, products }: { route: Extract<AppRoute, { kind: "crm" }>; navigate: (path: string) => void; onExpired: () => void; products: readonly ProductManifest[] }) {
  const api = useMemo(() => new CrmApiClient(fetch), []);
  return <Page className="crm-workspace" density="comfortable"><CrmNav section={route.section} navigate={navigate} />{route.section === "home" ? <CrmHome api={api} navigate={navigate} onExpired={onExpired} /> : route.section === "businesses" ? <CrmBusinesses api={api} navigate={navigate} onExpired={onExpired} /> : route.section === "business" ? <CrmBusinessDetail api={api} businessId={route.businessId!} products={products} navigate={navigate} onExpired={onExpired} /> : route.section === "pipeline" ? <CrmPipelineView api={api} navigate={navigate} onExpired={onExpired} /> : <CrmTasks api={api} navigate={navigate} onExpired={onExpired} />}</Page>;
}
